/**
 * Admin Routes
 * 관리자 페이지 라우팅 설정
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Layout
import { AdminLayout } from '../components/layout';

// Auth Pages
import {
  AdminLoginPage,
  AdminProfilePage,
  AdminPasswordChangePage,
} from '../components/auth';

// Dashboard
import { AdminDashboard } from '../components/dashboard';

// Organizations (개별 페이지는 BusinessManagementPage 탭으로 통합)
import { OrganizationListPage } from '../components/organizations';

// Sales
import { SalesListPage } from '../components/sales';

// Subscriptions - 신규 시스템 (실제 작동)
import { SubscriptionManagementPage } from '../../../pages/admin/SubscriptionManagementPage';

// Service Plans
import ServicePlansPage from '../components/pages/ServicePlansPage';

// Business Management (통합 페이지: 업체 + 구독 + 플랜)
import { BusinessManagementPage } from '../components/pages/BusinessManagementPage';


// Payments
import AdminPaymentDashboard from '../components/payments/AdminPaymentDashboard';

// Billing Plan Management
import { BillingPlanManagement } from '../components/billing/BillingPlanManagement';
import { CreditChargeRequestsPage } from '../components/billing/CreditChargeRequestsPage';

// Contracts
import { ContractListPage } from '../components/contracts';

// Servers
import { ServerManagementPage } from '../components/servers';

// Migrations
import { MigrationHistoryPage } from '../components/migrations';

// Devices
import DeviceManagementPage from '../../../pages/admin/DeviceManagementPage';

// Tax Management
import {
  TaxExpensePage,
  TaxRevenuePage,
  TaxInvoicePage,
  TaxDashboardPage,
  VatSummaryPage,
  CategorySettingsPage,
} from '../components/tax';

/**
 * Admin Routes Component
 *
 * 라우트 구조:
 * /admin/login - 로그인
 * /admin/dashboard - 대시보드
 * /admin/businesses - 사업자 관리
 * /admin/sales - 영업 관리 (홈페이지 문의 포함)
 * /admin/subscriptions - 구독 관리
 * /admin/reports - 통계 및 리포트
 * /admin/logs - 작업 로그
 * /admin/settings - 설정
 */
export const AdminRoutes: React.FC = () => {
  return (
    <Routes>
      {/* 관리자 페이지 (레이아웃 포함) */}
      <Route path="/" element={<AdminLayout />}>
        {/* 기본 경로 → 대시보드로 리다이렉트 */}
        <Route index element={<Navigate to="/admin/dashboard" replace />} />

        {/* 대시보드 */}
        <Route path="dashboard" element={<AdminDashboard />} />

        {/* 업체 관리 (통합: 사업자 + 구독 + 플랜) */}
        <Route path="businesses" element={<BusinessManagementPage />} />
        <Route path="businesses/pending" element={<BusinessManagementPage />} />
        <Route path="businesses/approved" element={<BusinessManagementPage />} />
        <Route path="businesses/rejected" element={<BusinessManagementPage />} />

        {/* 구독 관리 → 통합 페이지로 리다이렉트 */}
        <Route path="subscriptions" element={<Navigate to="/admin/businesses?tab=subscriptions" replace />} />

        {/* 플랜 관리 → 통합 페이지로 리다이렉트 */}
        <Route path="service-plans" element={<Navigate to="/admin/businesses?tab=plans" replace />} />

        {/* 문의 관리 → 영업 관리로 리다이렉트 (메뉴 통합) */}
        <Route path="inquiries" element={<Navigate to="/admin/sales?view=new_inquiry" replace />} />
        <Route path="inquiries/*" element={<Navigate to="/admin/sales?view=new_inquiry" replace />} />

        {/* 영업 관리 */}
        <Route path="sales" element={<SalesListPage />} />
        <Route path="sales/consulting" element={<SalesListPage />} />
        <Route path="sales/contracted" element={<SalesListPage />} />

        {/* 모듈 관리 → 제거됨 (비즈니스 모델 단순화) */}
        <Route path="modules" element={<Navigate to="/admin/dashboard" replace />} />

        {/* 통계/리포트, 작업 로그, 게이트 관리 → 제거됨 */}
        <Route path="reports/*" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="logs/*" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="gates" element={<Navigate to="/admin/dashboard" replace />} />

        {/* 결제 관리 */}
        <Route path="payments" element={<AdminPaymentDashboard />} />

        {/* 전자계약 관리 */}
        <Route path="contracts" element={<ContractListPage />} />

        {/* 서버 관리 */}
        <Route path="servers" element={<ServerManagementPage />} />

        {/* 서버 이전 이력 */}
        <Route path="migrations" element={<MigrationHistoryPage />} />

        {/* 기기 관리 */}
        <Route path="devices" element={<DeviceManagementPage />} />

        {/* 빌링 플랜 관리 */}
        <Route path="billing/plans" element={<BillingPlanManagement />} />

        {/* 크레딧 충전 요청 관리 */}
        <Route path="billing/credit-requests" element={<CreditChargeRequestsPage />} />

        {/* 세무 관리 */}
        <Route path="tax" element={<Navigate to="/admin/tax/dashboard" replace />} />
        <Route path="tax/dashboard" element={<TaxDashboardPage />} />
        <Route path="tax/expenses" element={<TaxExpensePage />} />
        <Route path="tax/revenues" element={<TaxRevenuePage />} />
        <Route path="tax/invoices" element={<TaxInvoicePage />} />
        <Route path="tax/vat" element={<VatSummaryPage />} />
        <Route path="tax/categories" element={<CategorySettingsPage />} />

        {/* 설정 */}
        <Route path="settings/profile" element={<AdminProfilePage />} />
        <Route path="settings/password" element={<AdminPasswordChangePage />} />
        {/* Operator 관리와 서비스 설정은 추후 구현 */}
      </Route>

      {/* 404 - 대시보드로 리다이렉트 */}
      <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
    </Routes>
  );
};
